import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const Shell = ({ title, subtitle, children, footer }) => (
  <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
    <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,#FFD100_0,transparent_45%),radial-gradient(circle_at_80%_80%,#00A3E0_0,transparent_50%)]" />
    <Card className="relative w-full max-w-md p-8 bg-slate-900/90 backdrop-blur border-slate-800 text-slate-100" data-testid="agent-auth-card">
      <div className="mb-6">
        <img src="/rozfa-logo-white.png" alt="Rozfa Print Ops" className="h-10 w-auto mb-3" />
        <h1 className="font-display font-bold text-xl tracking-tight">{title}</h1>
        <p className="text-xs text-slate-400 font-mono">{subtitle}</p>
      </div>
      {children}
      <div className="mt-6 pt-6 border-t border-slate-800 text-xs text-slate-400">{footer}</div>
    </Card>
  </div>
);

const inputCls = "bg-slate-800 border-slate-700 text-slate-100";

export function AgentLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.user.role !== "agent") { nav("/dashboard"); return; }
      toast.success(`Welcome, ${data.user.name}`);
      nav("/agent");
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally { setLoading(false); }
  };

  return (
    <Shell title="Rozfa Agent Portal" subtitle="For registered agents only"
      footer={<>Not registered yet? <Link to="/agent/register" className="text-amber-400 hover:underline" data-testid="agent-register-link">Register as an agent</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <div><Label className="text-slate-300">Email</Label><Input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="agent-login-email" /></div>
        <div><Label className="text-slate-300">Password</Label><Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="agent-login-password" /></div>
        <Button type="submit" disabled={loading} className="w-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold" data-testid="agent-login-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
        </Button>
      </form>
    </Shell>
  );
}

export function AgentRegister() {
  const empty = { company_name: "", email: "", address: "", ssm_no: "", tin_no: "", sst_no: "", pic_name: "", pic_phone: "", pic_email: "", password: "" };
  const [form, setForm] = useState(empty);
  const [ssmFile, setSsmFile] = useState(null);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!ssmFile) return toast.error("Please upload your SSM Certificate");
    if (!agree) return toast.error("Please agree to the Terms & Conditions");
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("agree_terms", "true");
      fd.append("ssm_file", ssmFile);
      await api.post("/agent/register", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err, "Registration failed"));
    } finally { setLoading(false); }
  };

  const field = (k, label, type = "text", required = false) => (
    <div><Label className="text-slate-300">{label}{required && " *"}</Label><Input type={type} required={required} value={form[k]} onChange={(e) => set(k, e.target.value)} className={inputCls} data-testid={`agent-reg-${k.replace(/_/g, "-")}`} /></div>
  );
  const section = (title) => <div className="text-[11px] font-mono uppercase tracking-wider text-amber-400 pt-2">{title}</div>;

  return (
    <Shell title="Agent Registration" subtitle="Applications are reviewed by Rozfa admin"
      footer={<>Already have an account? <Link to="/agent/login" className="text-amber-400 hover:underline" data-testid="agent-login-link">Sign in</Link></>}>
      {done ? (
        <div className="text-sm space-y-2" data-testid="agent-register-success">
          <div className="font-semibold text-amber-300">Registration received!</div>
          <p className="text-slate-300">Your account will be activated once approved by Rozfa admin. You will be able to sign in after that.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          {section("Company Details")}
          {field("company_name", "Company Name", "text", true)}
          {field("email", "Email (login)", "email", true)}
          {field("address", "Address", "text", true)}
          {field("ssm_no", "SSM No.", "text", true)}
          <div>
            <Label className="text-slate-300">SSM Certificate * (PDF / image)</Label>
            <Input type="file" required accept=".pdf,image/*" onChange={(e) => setSsmFile(e.target.files?.[0] || null)} className={`${inputCls} file:text-slate-300`} data-testid="agent-reg-ssm-file" />
            {ssmFile && <div className="text-xs text-slate-400 mt-1" data-testid="agent-reg-ssm-filename">{ssmFile.name}</div>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {field("tin_no", "TIN No.", "text", false)}
            {field("sst_no", "SST Reg No.", "text", false)}
          </div>
          {section("Person In Charge (PIC)")}
          {field("pic_name", "PIC Name", "text", true)}
          <div className="grid grid-cols-2 gap-3">
            {field("pic_phone", "PIC Tel No.", "text", true)}
            {field("pic_email", "PIC Email", "email", false)}
          </div>
          {section("Account")}
          {field("password", "Password (min 6)", "password", true)}
          <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer pt-1">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-amber-400" data-testid="agent-reg-agree" />
            <span>I agree to the <span className="text-amber-400">Terms &amp; Conditions</span> *</span>
          </label>
          <Button type="submit" disabled={loading || !agree} className="w-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold disabled:opacity-50" data-testid="agent-reg-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Registration"}
          </Button>
        </form>
      )}
    </Shell>
  );
}
